import type { BusinessAddress } from "../types/organization/Organization";
import type { GeoAddress } from "./location";

// Field list used for structural equality. Avoids JSON.stringify pitfalls —
// PostgreSQL JSONB returns keys in alphabetical order, while
// geoAddressToBusinessAddress emits them in declaration order, so a stringify
// round-trip always reports "dirty" even when the content is identical.
const BUSINESS_ADDRESS_KEYS: ReadonlyArray<keyof BusinessAddress> = [
	"formattedAddress",
	"placeId",
	"latitude",
	"longitude",
	"streetNumber",
	"street",
	"city",
	"state",
	"postalCode",
	"country"
];

export function businessAddressesEqual(
	a: BusinessAddress | null | undefined,
	b: BusinessAddress | null | undefined
): boolean {
	if (!a && !b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}
	for (const key of BUSINESS_ADDRESS_KEYS) {
		if ((a[key] ?? null) !== (b[key] ?? null)) {
			return false;
		}
	}
	return true;
}

export function businessAddressToGeoAddress(address: BusinessAddress | null | undefined): GeoAddress | null {
	if (!address || !address.formattedAddress) {
		return null;
	}

	return {
		distance: "",
		formattedAddress: address.formattedAddress,
		latitude: address.latitude ?? 0,
		longitude: address.longitude ?? 0,
		placeId: address.placeId ?? "",
		streetNumber: address.streetNumber,
		street: address.street,
		city: address.city,
		state: address.state,
		postalCode: address.postalCode,
		country: address.country
	};
}

export function geoAddressToBusinessAddress(geo: GeoAddress | null | undefined): BusinessAddress | null {
	if (!geo || !geo.formattedAddress) {
		return null;
	}

	return {
		formattedAddress: geo.formattedAddress,
		placeId: geo.placeId || null,
		latitude: typeof geo.latitude === "number" ? geo.latitude : null,
		longitude: typeof geo.longitude === "number" ? geo.longitude : null,
		streetNumber: geo.streetNumber ?? null,
		street: geo.street ?? null,
		city: geo.city ?? null,
		state: geo.state ?? null,
		postalCode: geo.postalCode ?? null,
		country: geo.country ?? null
	};
}
